import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Factory, Link2, ListChecks, Package, RefreshCw, Store } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminPage, EmptyState, KpiCard, formatNumber } from '../../../components/design-system/admin';
import { networkErrorMessage, useNetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import type { NetworkStats, RetailerSummary } from '../../../common/hooks/network-admin.types';
import { NetworkActions } from '../../../modules/superadmin/network/NetworkActions';
import { CoverageTable, LastEvalCard, LinkBreakdownCard, ReviewAcceptanceCard, SurfaceIdCard } from '../../../modules/superadmin/network/NetworkStatsCards';
import { formatShare } from '../../../modules/superadmin/network/network-helpers';

/**
 * The Ardealy Network, for the people who run it: how big the graph is, how
 * much of each brand is covered, how good matching is (the rig evaluation and
 * what reviewers decided), and the three levers — run the matcher, discover
 * products several shops carry, backfill SurfaceID vectors.
 */
export function NetworkOverviewPage() {
    const { t } = useTranslation();
    const api = useNetworkAdminApi();
    const [stats, setStats] = useState<NetworkStats | null>(null);
    const [retailers, setRetailers] = useState<RetailerSummary[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            const s = await api.stats();
            if (!s?.brands) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
            setStats(s);
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_SHELL.COMMON.LOAD_FAILED'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api]);

    useEffect(() => {
        load();
        api.retailers().then(setRetailers).catch(() => setRetailers([]));
    }, [api, load]);

    const refresh = async () => {
        setRefreshing(true);
        await load();
        setRefreshing(false);
    };

    const s = stats;
    const suggested = s?.links?.byStatus?.suggested ?? 0;

    return (
        <AdminPage
            title={t('ADMIN_NETWORK.NAV')}
            parents={[{ label: t('ADMIN_SHELL.NAV.SECTION_CATALOG') }]}
            description={t('ADMIN_NETWORK.OVERVIEW.DESCRIPTION')}
            actions={
                <>
                    <Button variant="ghost" size="icon-sm" onClick={refresh} disabled={refreshing} aria-label={t('ADMIN_NETWORK.COMMON.REFRESH')}>
                        <RefreshCw className={refreshing ? 'animate-spin' : undefined} />
                    </Button>
                    <Button variant="outline" asChild>
                        <Link to="/network/brands">
                            <Factory />
                            {t('ADMIN_NETWORK.NAV_BRANDS')}
                        </Link>
                    </Button>
                    <Button variant="outline" asChild data-testid="overview-review">
                        <Link to="/network/review">
                            <ListChecks />
                            {suggested ? t('ADMIN_NETWORK.OVERVIEW.REVIEW_N', { count: suggested }) : t('ADMIN_NETWORK.NAV_REVIEW')}
                        </Link>
                    </Button>
                </>
            }
        >
          {/* `contents`: a hook for tests that leaves the page's own layout alone. */}
          <div className="contents" data-testid="network-overview">
            {error && !s ? (
                <EmptyState
                    title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                    description={error}
                    action={
                        <Button variant="outline" onClick={load}>
                            {t('ADMIN_SHELL.COMMON.RETRY')}
                        </Button>
                    }
                    className="py-24"
                />
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5" data-testid="network-kpis">
                        <KpiCard
                            label={t('ADMIN_NETWORK.KPI.BRANDS')}
                            value={s ? formatNumber(s.brands.total) : ''}
                            hint={s ? t('ADMIN_NETWORK.KPI.BRANDS_HINT', { partner: s.brands.byStatus?.partner ?? 0, claimed: s.brands.byStatus?.claimed ?? 0, unclaimed: s.brands.byStatus?.unclaimed ?? 0 }) : undefined}
                            icon={<Factory />}
                            href="/network/brands"
                            loading={!s}
                            testId="kpi-brands"
                        />
                        <KpiCard
                            label={t('ADMIN_NETWORK.KPI.PRODUCTS')}
                            value={s ? formatNumber(s.products.total) : ''}
                            hint={s ? t('ADMIN_NETWORK.KPI.PRODUCTS_HINT', { published: s.products.byStatus?.published ?? 0, collections: s.collections?.total ?? 0 }) : undefined}
                            icon={<Package />}
                            loading={!s}
                            testId="kpi-products"
                        />
                        <KpiCard
                            label={t('ADMIN_NETWORK.KPI.DIGITIZED')}
                            value={s ? formatShare(s.products.digitizedShare) : ''}
                            hint={s ? t('ADMIN_NETWORK.KPI.DIGITIZED_HINT', { count: s.products.digitized ?? 0 }) : undefined}
                            icon={<Package />}
                            loading={!s}
                            testId="kpi-digitized"
                        />
                        <KpiCard
                            label={t('ADMIN_NETWORK.KPI.LINKS')}
                            value={s ? formatNumber(s.links.byStatus?.confirmed ?? 0) : ''}
                            hint={s ? t('ADMIN_NETWORK.KPI.LINKS_HINT', { count: suggested }) : undefined}
                            icon={<Link2 />}
                            href="/network/review"
                            loading={!s}
                            testId="kpi-links"
                        />
                        <KpiCard
                            label={t('ADMIN_NETWORK.KPI.RETAILERS')}
                            value={s ? formatNumber(s.retailers.linked) : ''}
                            hint={s ? t('ADMIN_NETWORK.KPI.RETAILERS_HINT', { total: s.retailers.total }) : undefined}
                            icon={<Store />}
                            loading={!s}
                            testId="kpi-retailers"
                        />
                    </div>

                    {s ? (
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                            <SurfaceIdCard stats={s} />
                            <LinkBreakdownCard stats={s} />
                            <ReviewAcceptanceCard stats={s} />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 w-full rounded-xl" />)}
                        </div>
                    )}

                    {error && s && <p className="text-sm text-destructive" role="alert">{error}</p>}

                    <NetworkActions api={api} retailers={retailers} onChanged={load} />

                    {s && <CoverageTable stats={s} />}
                    {s && <LastEvalCard stats={s} />}
                </>
            )}
          </div>
        </AdminPage>
    );
}

export default NetworkOverviewPage;
